export type DonutTone = "sky" | "emerald" | "amber" | "red" | "slate";

export interface DonutSlice {
  label: string;
  value: number;
  tone: DonutTone;
}

interface DonutChartProps {
  slices: DonutSlice[];
  centerLabel?: string;
  emptyMessage?: string;
}

// Arc strokes pick up `currentColor` from per-arc utility classes so the
// theme switch flips them automatically without inline-style HEX maps.
const ARC_COLOR_CLASS: Record<DonutTone, string> = {
  sky: "text-arc-sky",
  emerald: "text-arc-emerald",
  amber: "text-arc-amber",
  red: "text-arc-red",
  slate: "text-arc-slate",
};

const LEGEND_DOT: Record<DonutTone, string> = {
  sky: "bg-arc-sky",
  emerald: "bg-arc-emerald",
  amber: "bg-arc-amber",
  red: "bg-arc-red",
  slate: "bg-arc-slate",
};

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function DonutChart({
  slices,
  centerLabel,
  emptyMessage = "No data",
}: DonutChartProps) {
  const total = slices.reduce((acc, s) => acc + Math.max(0, s.value), 0);

  if (total === 0) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-border-subtle px-3 py-8 text-center text-sm text-fg-faint">
        {emptyMessage}
      </div>
    );
  }

  const positiveSlices = slices.filter((s) => s.value > 0);
  const cumulativeBefore = positiveSlices.reduce<number[]>(
    (acc, s) => {
      const prev = acc[acc.length - 1] ?? 0;
      acc.push(prev + s.value);
      return acc;
    },
    [0],
  );
  const arcs = positiveSlices.map((s, idx) => {
    const fraction = s.value / total;
    const length = fraction * CIRCUMFERENCE;
    const offset = -((cumulativeBefore[idx] / total) * CIRCUMFERENCE);
    return { ...s, length, offset };
  });

  return (
    <div className="flex items-center gap-5">
      <svg
        viewBox="0 0 100 100"
        className="h-32 w-32 shrink-0 -rotate-90"
        role="img"
        aria-label="Donut chart"
      >
        <circle
          cx="50"
          cy="50"
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth="14"
          className="text-chart-grid"
        />
        {arcs.map((a) => (
          <circle
            key={a.label}
            cx="50"
            cy="50"
            r={RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth="14"
            strokeDasharray={`${a.length.toFixed(3)} ${CIRCUMFERENCE.toFixed(3)}`}
            strokeDashoffset={a.offset.toFixed(3)}
            strokeLinecap="butt"
            className={ARC_COLOR_CLASS[a.tone]}
          />
        ))}
      </svg>
      <ul className="flex min-w-0 flex-col gap-1.5 text-xs">
        {centerLabel && (
          <li className="mb-1 text-[11px] font-medium uppercase tracking-wide text-fg-faint">
            {centerLabel}
          </li>
        )}
        {slices.map((s) => {
          const pct = total === 0 ? 0 : (s.value / total) * 100;
          return (
            <li key={s.label} className="flex items-center gap-2">
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${LEGEND_DOT[s.tone]}`}
                aria-hidden="true"
              />
              <span className="truncate text-neutral-text">{s.label}</span>
              <span className="ml-auto tabular-nums text-neutral-text-strong">
                {s.value.toLocaleString("en-US")}
              </span>
              <span className="w-12 text-right tabular-nums text-fg-faint">
                {pct.toFixed(1)}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
